<template>
    <PageHeader :crumbs="[{ label: $t('dashboard.sidebar.dashboard') }]" />

    <div class="tw-p-2 tw-space-y-3">
        <!-- Stat cards (colorful) -->
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-3">
            <div v-for="card in cards" :key="card.key"
                class="tw-relative tw-overflow-hidden tw-rounded-xl tw-p-4 tw-text-white tw-shadow-lg tw-bg-gradient-to-br"
                :class="card.gradient">
                <icon :icon="card.icon" class="tw-absolute tw-right-2 tw-top-2 tw-text-6xl tw-opacity-20" />
                <div class="tw-relative">
                    <div class="tw-p-2 tw-w-fit tw-rounded-lg tw-bg-white/20 tw-mb-3">
                        <icon :icon="card.icon" class="tw-text-xl" />
                    </div>
                    <p class="tw-text-xs tw-uppercase tw-font-semibold tw-text-white/80">{{ card.label }}</p>
                    <p class="tw-text-2xl tw-font-bold">{{ card.value }}</p>
                </div>
            </div>
        </div>

        <!-- Charts -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-3">
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-3">
                <div class="tw-flex tw-items-start tw-justify-between tw-gap-2">
                    <div>
                        <h3 class="tw-font-semibold tw-text-sm tw-text-gray-700">{{ $t('portal.charts.revenue-expense') }}</h3>
                        <p class="tw-text-[11px] tw-text-gray-400 tw-mb-2">{{ $t('portal.charts.last-6-months') }} · {{ $t('portal.charts.in-eur') }}</p>
                    </div>
                    <!-- EUR -> MAD rate editor -->
                    <div class="tw-flex tw-items-center tw-gap-1 tw-shrink-0 tw-bg-gray-50 tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-px-2 tw-py-1">
                        <span class="tw-text-[11px] tw-font-semibold tw-text-gray-500">1 € =</span>
                        <input v-model="rateInput" @change="applyRate" @keyup.enter="applyRate" type="number" step="0.01" min="0"
                            class="tw-w-16 tw-text-[12px] tw-font-semibold tw-text-gray-700 tw-text-right tw-bg-transparent tw-outline-none" />
                        <span class="tw-text-[11px] tw-font-semibold tw-text-gray-500">MAD</span>
                    </div>
                </div>
                <AreaLineChart :series="revenueSeries" :labels="chartLabels" :format="eur" :height="150" />
            </div>
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-3">
                <h3 class="tw-font-semibold tw-text-sm tw-text-gray-700">{{ $t('portal.charts.new-purchases') }}</h3>
                <p class="tw-text-[11px] tw-text-gray-400 tw-mb-2">{{ $t('portal.charts.last-6-months') }}</p>
                <BarChart :items="purchasesItems" color="#8b5cf6" :height="150" />
            </div>
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-3">
                <h3 class="tw-font-semibold tw-text-sm tw-text-gray-700">{{ $t('portal.charts.subscription-status') }}</h3>
                <p class="tw-text-[11px] tw-text-gray-400 tw-mb-2">{{ $t('portal.charts.all-purchases') }}</p>
                <DonutChart :data="statusData" :center-label="$t('portal.fields.products')" />
            </div>
        </div>

        <!-- Recent customers / purchases -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-3">
            <!-- Recent customers -->
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-4">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                        <icon icon="solar:users-group-rounded-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                        {{ $t('portal.dashboard.recent-customers') }}
                        <span v-if="stats.total_clients > 0" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ stats.total_clients }}</span>
                    </h3>
                    <router-link :to="{ name: 'admin.customers' }" class="tw-text-xs tw-font-semibold tw-text-primary-500 hover:tw-text-primary-600 tw-inline-flex tw-items-center tw-gap-1">
                        {{ $t('portal.dashboard.show-more') }} <icon icon="solar:arrow-right-linear" />
                    </router-link>
                </div>
                <ul class="tw-divide-y">
                    <li v-for="c in recentCustomers" :key="c.id" class="tw-py-2 tw-flex tw-items-center tw-justify-between">
                        <div>
                            <p class="tw-text-sm tw-font-medium tw-text-gray-800">{{ c.name }}</p>
                            <p class="tw-text-xs tw-text-gray-400">{{ c.email || '—' }}</p>
                        </div>
                        <StatusBadge :status="c.status" />
                    </li>
                    <li v-if="!recentCustomers.length" class="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">{{ $t('messages.table-no-results') }}</li>
                </ul>
            </div>

            <!-- Recent purchases -->
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-4">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                        <icon icon="solar:cart-large-minimalistic-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                        {{ $t('portal.dashboard.recent-purchases') }}
                        <span v-if="recentPurchases.length" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ recentPurchases.length }}</span>
                    </h3>
                    <router-link :to="{ name: 'admin.customers' }" class="tw-text-xs tw-font-semibold tw-text-primary-500 hover:tw-text-primary-600 tw-inline-flex tw-items-center tw-gap-1">
                        {{ $t('portal.dashboard.show-more') }} <icon icon="solar:arrow-right-linear" />
                    </router-link>
                </div>
                <ul class="tw-divide-y">
                    <li v-for="p in recentPurchases" :key="p.id" class="tw-py-2 tw-flex tw-items-center tw-justify-between">
                        <div>
                            <p class="tw-text-sm tw-font-medium tw-text-gray-800">{{ p.product_name }}</p>
                            <p class="tw-text-xs tw-text-gray-400">{{ p.customer_name }} · {{ eur(p.price) }}</p>
                        </div>
                        <StatusBadge :status="p.status" />
                    </li>
                    <li v-if="!recentPurchases.length" class="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">{{ $t('messages.table-no-results') }}</li>
                </ul>
            </div>
        </div>

        <!-- New tickets / recent notifications / expiring soon -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-3">
            <!-- New / open tickets -->
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-4">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                        <icon icon="solar:chat-round-line-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                        {{ $t('portal.dashboard.new-tickets') }}
                        <span v-if="stats.open_tickets > 0" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ stats.open_tickets }}</span>
                    </h3>
                    <router-link :to="{ name: 'admin.tickets' }" class="tw-text-xs tw-font-semibold tw-text-primary-500 hover:tw-text-primary-600 tw-inline-flex tw-items-center tw-gap-1">
                        {{ $t('portal.dashboard.show-more') }} <icon icon="solar:arrow-right-linear" />
                    </router-link>
                </div>
                <ul class="tw-divide-y">
                    <li v-for="tk in recentTickets" :key="tk.id"
                        class="tw-py-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3 tw-cursor-pointer hover:tw-bg-gray-50 tw-rounded tw-px-1"
                        @click="openTicket(tk)">
                        <div class="tw-min-w-0">
                            <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ tk.subject }}</p>
                            <p class="tw-text-xs tw-text-gray-400 tw-truncate">{{ tk.customer_name }}</p>
                        </div>
                        <TicketStatusBadge :status="tk.status" />
                    </li>
                    <li v-if="!recentTickets.length" class="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">{{ $t('portal.tickets.none-admin') }}</li>
                </ul>
            </div>

            <!-- Recent notifications -->
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-4">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                        <icon icon="solar:bell-bing-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                        {{ $t('portal.dashboard.recent-notifications') }}
                        <span v-if="recentNotifications.length" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ recentNotifications.length }}</span>
                    </h3>
                    <router-link :to="{ name: 'admin.notifications' }" class="tw-text-xs tw-font-semibold tw-text-primary-500 hover:tw-text-primary-600 tw-inline-flex tw-items-center tw-gap-1">
                        {{ $t('portal.dashboard.show-more') }} <icon icon="solar:arrow-right-linear" />
                    </router-link>
                </div>
                <ul class="tw-divide-y">
                    <li v-for="n in recentNotifications" :key="n.id" class="tw-py-2.5">
                        <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ n.title }}</p>
                        <p class="tw-text-xs tw-text-gray-400 tw-flex tw-items-center tw-gap-1">
                            <icon icon="solar:users-group-rounded-bold-duotone" /> {{ n.recipients_count }} · {{ fmt.datetime(n.created_at) }}
                        </p>
                    </li>
                    <li v-if="!recentNotifications.length" class="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">{{ $t('messages.table-no-results') }}</li>
                </ul>
            </div>

            <!-- Expiring soon -->
            <div class="tw-bg-white tw-rounded-xl tw-border tw-border-b-2 tw-border-solid tw-p-4">
                <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
                    <h3 class="tw-font-semibold tw-text-gray-700 tw-flex tw-items-center tw-gap-2">
                        <icon icon="solar:clock-circle-bold-duotone" class="tw-text-primary-500 tw-text-xl" />
                        {{ $t('portal.dashboard.expiring-soon') }}
                        <span v-if="expiringSoon.length" class="tw-text-xs tw-bg-primary-500 tw-text-white tw-rounded-full tw-px-2 tw-py-0.5">{{ expiringSoon.length }}</span>
                    </h3>
                </div>
                <ul class="tw-divide-y">
                    <li v-for="p in expiringSoon" :key="p.id" class="tw-py-2.5 tw-flex tw-items-center tw-justify-between tw-gap-3">
                        <div class="tw-min-w-0">
                            <p class="tw-text-sm tw-font-medium tw-text-gray-800 tw-truncate">{{ p.product_name }}</p>
                            <p class="tw-text-xs tw-text-gray-400 tw-truncate">{{ p.customer_name }} · {{ fmt.date(p.expiry_date) }}</p>
                        </div>
                        <StatusBadge :status="p.status" />
                    </li>
                    <li v-if="!expiringSoon.length" class="tw-py-4 tw-text-center tw-text-sm tw-text-gray-400">{{ $t('messages.table-no-results') }}</li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { stats as fetchStats } from '@/api/dashboard';
import PageHeader from '@/components/PageHeader.vue';
import StatusBadge from '@/components/StatusBadge.vue';
import TicketStatusBadge from '@/views/admin/tickets/TicketStatusBadge.vue';
import AreaLineChart from '@/components/charts/AreaLineChart.vue';
import BarChart from '@/components/charts/BarChart.vue';
import DonutChart from '@/components/charts/DonutChart.vue';
import { useFormat } from '@/composables/useFormat';
import { useCurrency } from '@/composables/useCurrency';
import { usePortalStore } from '@/stores';

const { t } = useI18n();
const fmt = useFormat();
const { rate, setRate, eur, mad, madToEur } = useCurrency();
const router = useRouter();
const portalStore = usePortalStore();

// Rate editor (EUR -> MAD).
const rateInput = ref(rate.value);
const applyRate = () => setRate(rateInput.value);

const stats = ref({ total_revenue: 0, total_clients: 0, active_subscriptions: 0, total_expense: 0, open_tickets: 0 });
const recentCustomers = ref([]);
const recentPurchases = ref([]);
const recentNotifications = ref([]);
const expiringSoon = ref([]);
const recentTickets = ref([]);
const charts = ref({ revenue_series: [], purchases_series: [], purchase_status: { active: 0, expired: 0, cancelled: 0 } });

const openTicket = (tk) => router.push({ name: 'admin.tickets.show', params: { id: tk.id } });

const cards = computed(() => [
    { key: 'revenue', label: t('portal.stats.total-revenue'), value: eur(stats.value.total_revenue), icon: 'solar:wallet-money-bold-duotone', gradient: 'tw-from-emerald-500 tw-to-emerald-700' },
    { key: 'clients', label: t('portal.stats.total-clients'), value: stats.value.total_clients, icon: 'solar:users-group-rounded-bold-duotone', gradient: 'tw-from-primary-500 tw-to-primary-700' },
    { key: 'subs', label: t('portal.stats.active-subscriptions'), value: stats.value.active_subscriptions, icon: 'solar:refresh-circle-bold-duotone', gradient: 'tw-from-cyan-500 tw-to-blue-600' },
    { key: 'expense', label: t('portal.stats.total-expense'), value: mad(stats.value.total_expense), icon: 'solar:cart-large-minimalistic-bold-duotone', gradient: 'tw-from-amber-500 tw-to-orange-600' },
]);

const chartLabels = computed(() => charts.value.revenue_series.map(p => p.label));
// Both lines shown in EUR for a fair comparison (expenses are converted from MAD).
const revenueSeries = computed(() => [
    { name: t('portal.stats.total-revenue'), color: '#10b981', points: charts.value.revenue_series.map(p => Number(p.revenue)) },
    { name: t('portal.stats.total-expense'), color: '#f43f5e', points: charts.value.revenue_series.map(p => madToEur(p.expense)) },
]);
const purchasesItems = computed(() => charts.value.purchases_series.map(p => ({ label: p.label, value: p.count })));
const statusData = computed(() => [
    { label: t('portal.status.active'), value: charts.value.purchase_status.active, color: '#10b981' },
    { label: t('portal.status.expired'), value: charts.value.purchase_status.expired, color: '#f59e0b' },
    { label: t('portal.status.cancelled'), value: charts.value.purchase_status.cancelled, color: '#f43f5e' },
]);

async function load() {
    try {
        const { data } = await fetchStats();
        stats.value = data.stats;
        recentCustomers.value = data.recent_customers || [];
        recentPurchases.value = data.recent_purchases || [];
        recentNotifications.value = data.recent_notifications || [];
        expiringSoon.value = data.expiring_soon || [];
        recentTickets.value = data.recent_tickets || [];
        if (data.charts) charts.value = data.charts;
        // Keep the sidebar Support badge in sync.
        portalStore.setOpenTickets(data.stats?.open_tickets || 0);
    } catch (e) {
        // handled by interceptor
    }
}

load();
</script>
